import { BASE_URL, fetchWithAuthRetry, getToken, request, uploadWithProgress } from '@/api/client';
import type { VideoAvatarStatus } from '@/types/video_avatar';
import type { TrendingEchoItem } from '@/types/echo';
import type {
  CreateEchoDraftRequest,
  EchoDraftResponse,
  UpdateSocialLinksRequest,
  SubmitPersonalityTestRequest,
  PersonalityConfig,
  AddTrainingLinksRequest,
  PublishEchoResponse,
  TrainEchoResponse,
  UpdateVisibilityRequest,
  UpdatePricingRequest,
  EchoProfileResponse,
  TrainingFile,
  UploadedFile,
  CollectInternetResponse,
  UpdateBasicInfoRequest,
  PartialPersonalityTestRequest,
  EchoListResponse,
  AISuggestResponse,
  VoiceClone,
  VoiceCloneResponse,
  VoiceCloneOptions,
  DraftAvatarResponse,
  TrainingStatusResponse,
  IdleVideoStatusResponse,
} from '@/types/echo_creation';
import type { RetrainEchoResponse } from '@/types/api_types';

/** Shared SSE line-based reader for echo service streams. */
async function readSSELines(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: string, data: Record<string, unknown>) => void
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6)) as Record<string, unknown>;
            onEvent(currentEvent, data);
          } catch {
            // skip malformed JSON
          }
        } else if (line.trim() === '') {
          currentEvent = 'message';
        }
      }
    }
  } finally {
    reader.cancel().catch(() => {
      /* stream already closed */
    });
  }
}

/** Service layer for all Echo Creation API endpoints. */
export const echoService = {
  /** Create a new echo draft. */
  createDraft: (data: CreateEchoDraftRequest): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>('/echoes/create', {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),

  /** Update basic info (name, domain, category, bio) for an existing draft. */
  updateBasicInfo: (draftId: string, data: UpdateBasicInfoRequest): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/basic-info`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  updateSocialLinks: (
    draftId: string,
    data: UpdateSocialLinksRequest
  ): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/social-links`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  submitPersonalityTest: (
    draftId: string,
    data: SubmitPersonalityTestRequest
  ): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/personality-test`, {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),

  /** Save partial personality test progress without completing the step. */
  savePartialPersonalityTest: (
    draftId: string,
    data: PartialPersonalityTestRequest
  ): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/partial-personality-test`, {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),

  updatePersonalityConfig: (draftId: string, data: PersonalityConfig): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/personality-config`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  getPersonalityPreview: (draftId: string): Promise<PersonalityConfig> =>
    request<PersonalityConfig>(`/echoes/${encodeURIComponent(draftId)}/personality-preview`, {
      method: 'GET',
      auth: true,
    }),

  addTrainingLinks: (draftId: string, data: AddTrainingLinksRequest): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/training-links`, {
      method: 'POST',
      body: JSON.stringify(data),
      auth: true,
    }),

  removeTrainingLink: (draftId: string, linkId: string): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(
      `/echoes/${encodeURIComponent(draftId)}/training-links/${encodeURIComponent(linkId)}`,
      {
        method: 'DELETE',
        auth: true,
      }
    ),

  uploadTrainingFile: (
    draftId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);
    const path = `/echoes/${encodeURIComponent(draftId)}/training-files`;
    return uploadWithProgress<UploadedFile>(path, formData, onProgress);
  },

  toggleScreenshotFlag: (
    draftId: string,
    fileId: string,
    containsScreenshots: boolean
  ): Promise<{ contains_screenshots: boolean }> =>
    request<{ contains_screenshots: boolean }>(
      `/echoes/${encodeURIComponent(draftId)}/training-files/${encodeURIComponent(fileId)}/screenshot-flag`,
      {
        method: 'PATCH',
        body: JSON.stringify({ contains_screenshots: containsScreenshots }),
        auth: true,
      }
    ),

  uploadDraftAvatar: (
    draftId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<DraftAvatarResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const path = `/echoes/${encodeURIComponent(draftId)}/draft-avatar`;
    return uploadWithProgress<DraftAvatarResponse>(path, formData, onProgress);
  },

  listTrainingFiles: (draftId: string): Promise<{ files: TrainingFile[]; total: number }> =>
    request<{ files: TrainingFile[]; total: number }>(
      `/echoes/${encodeURIComponent(draftId)}/training-files`,
      {
        method: 'GET',
        auth: true,
      }
    ),

  deleteTrainingFile: (draftId: string, fileId: string): Promise<void> =>
    request<void>(
      `/echoes/${encodeURIComponent(draftId)}/training-files/${encodeURIComponent(fileId)}`,
      {
        method: 'DELETE',
        auth: true,
      }
    ),

  /** Fetch supported languages and accents for voice cloning. */
  getVoiceCloneOptions: (): Promise<VoiceCloneOptions> =>
    request<VoiceCloneOptions>('/echoes/voice-clone-options', {
      method: 'GET',
      auth: false,
    }),

  /** Upload a voice sample to create a voice clone. */
  uploadVoiceClone: (
    draftId: string,
    file: File,
    onProgress?: (percent: number) => void,
    language?: string,
    accent?: string
  ): Promise<VoiceCloneResponse> => {
    const formData = new FormData();
    formData.append('voice_file', file);
    if (language) formData.append('language', language);
    if (accent) formData.append('accent', accent);
    const path = `/echoes/${encodeURIComponent(draftId)}/voice-clone`;
    return uploadWithProgress<VoiceCloneResponse>(path, formData, onProgress);
  },

  /** List all voice clones associated with a draft. */
  listVoiceClones: (draftId: string): Promise<{ voice_clones: VoiceClone[] }> =>
    request<{ voice_clones: VoiceClone[] }>(`/echoes/${encodeURIComponent(draftId)}/voice-clones`, {
      method: 'GET',
      auth: true,
    }),

  /** List voice clones for a published echo. */
  listVoiceClonesByEcho: (echoId: string): Promise<{ voice_clones: VoiceClone[] }> =>
    request<{ voice_clones: VoiceClone[] }>(
      `/echoes/published/${encodeURIComponent(echoId)}/voice-clones`,
      { method: 'GET', auth: true }
    ),

  /** Test a voice clone for a published echo. Returns raw MP3 Blob. */
  testVoiceCloneByEcho: async (echoId: string, cloneId: string, text?: string): Promise<Blob> => {
    const token = getToken();
    const baseUrl = BASE_URL;
    const params = new URLSearchParams();
    if (text) params.set('test_text', text);
    const qs = params.toString();
    const url = `${baseUrl}/echoes/published/${encodeURIComponent(echoId)}/voice-clone/${encodeURIComponent(cloneId)}/test${qs ? `?${qs}` : ''}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'audio/mpeg',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!resp.ok) {
      let detail = `Voice test failed: ${resp.status}`;
      try {
        const body = (await resp.json()) as Record<string, unknown>;
        if (body['detail']) detail = String(body['detail']);
      } catch {
        // response wasn't JSON
      }
      throw new Error(detail);
    }

    return resp.blob();
  },

  /** Delete a specific voice clone. */
  deleteVoiceClone: (draftId: string, cloneId: string): Promise<void> =>
    request<void>(
      `/echoes/${encodeURIComponent(draftId)}/voice-clones/${encodeURIComponent(cloneId)}`,
      {
        method: 'DELETE',
        auth: true,
      }
    ),

  /** Test a voice clone by generating audio from text. Returns raw MP3 Blob. */
  testVoiceClone: async (draftId: string, cloneId: string, text?: string): Promise<Blob> => {
    const token = getToken();
    const baseUrl = BASE_URL;
    const params = new URLSearchParams();
    if (text) params.set('test_text', text);
    const qs = params.toString();
    const url = `${baseUrl}/echoes/${encodeURIComponent(draftId)}/voice-clone/${encodeURIComponent(cloneId)}/test${qs ? `?${qs}` : ''}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'audio/mpeg',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!resp.ok) {
      let detail = `Voice test failed: ${resp.status}`;
      try {
        const body = (await resp.json()) as Record<string, unknown>;
        if (body['detail']) detail = String(body['detail']);
      } catch {
        // response wasn't JSON
      }
      throw new Error(detail);
    }

    return resp.blob();
  },

  aiSuggest: (draftId: string, field: string): Promise<AISuggestResponse> =>
    request<AISuggestResponse>(`/echoes/${encodeURIComponent(draftId)}/ai-suggest`, {
      method: 'POST',
      body: JSON.stringify({ field }),
      auth: true,
    }),

  aiSuggestBio: (options: {
    name: string;
    domain: string;
    category: string;
    additional_context?: string;
    is_public_figure?: boolean;
  }): Promise<AISuggestResponse> =>
    request<AISuggestResponse>('/echoes/ai-suggest-bio', {
      method: 'POST',
      body: JSON.stringify({
        name: options.name,
        domain: options.domain,
        category: options.category,
        additional_context: options.additional_context ?? '',
        is_public_figure: options.is_public_figure ?? false,
      }),
      auth: true,
    }),

  getDraftProfile: (draftId: string): Promise<EchoDraftResponse> =>
    request<EchoDraftResponse>(`/echoes/${encodeURIComponent(draftId)}/profile`, {
      method: 'GET',
      auth: true,
    }),

  publish: (draftId: string, isPublic?: boolean): Promise<PublishEchoResponse> =>
    request<PublishEchoResponse>(`/echoes/${encodeURIComponent(draftId)}/publish`, {
      method: 'POST',
      body: isPublic !== undefined ? JSON.stringify({ is_public: isPublic }) : null,
      auth: true,
    }),

  train: (draftId: string, collectTier?: 'free' | 'premium'): Promise<TrainEchoResponse> =>
    request<TrainEchoResponse>(`/echoes/${encodeURIComponent(draftId)}/train`, {
      method: 'POST',
      body: collectTier ? JSON.stringify({ collect_tier: collectTier }) : null,
      auth: true,
    }),

  /** Backward-compatible JSON retrain endpoint. New UI code uses retrainStream. */
  retrain: (
    draftId: string,
    collectTier: 'free' | 'premium' = 'free'
  ): Promise<RetrainEchoResponse> =>
    request<RetrainEchoResponse>(`/echoes/${encodeURIComponent(draftId)}/retrain`, {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ collect_tier: collectTier }),
    }),

  /** Retrain a published Echo with real backend progress over SSE. */
  retrainStream: (
    draftId: string,
    collectTier: 'free' | 'premium',
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const url = `${BASE_URL}/echoes/${encodeURIComponent(draftId)}/retrain-stream`;

    return fetchWithAuthRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ collect_tier: collectTier }),
      signal: signal ?? null,
    }).then(async (response) => {
      if (!response.ok) {
        let errBody: Record<string, unknown> = {};
        try {
          errBody = (await response.json()) as Record<string, unknown>;
        } catch {
          // ignore
        }
        throw Object.assign(
          new Error(String(errBody['detail'] ?? errBody['message'] ?? response.statusText)),
          { ...errBody, status: response.status }
        );
      }
      const reader = response.body?.getReader();
      if (!reader) return;
      await readSSELines(reader, onEvent);
    });
  },

  /** Start training via SSE stream (POST). */
  trainStream: (
    draftId: string,
    collectTier: 'free' | 'premium',
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const baseUrl = BASE_URL;
    const url = `${baseUrl}/echoes/${encodeURIComponent(draftId)}/train-stream`;

    return fetchWithAuthRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ collect_tier: collectTier }),
      signal: signal ?? null,
    }).then(async (response) => {
      if (!response.ok) {
        let errBody: Record<string, unknown> = {};
        try {
          errBody = (await response.json()) as Record<string, unknown>;
        } catch {
          // ignore
        }
        throw Object.assign(
          new Error(String(errBody['detail'] ?? errBody['message'] ?? response.statusText)),
          { ...errBody, status: response.status }
        );
      }
      const reader = response.body?.getReader();
      if (!reader) return;
      await readSSELines(reader, onEvent);
    });
  },

  publishTrained: (draftId: string, isPublic: boolean): Promise<PublishEchoResponse> =>
    request<PublishEchoResponse>(`/echoes/${encodeURIComponent(draftId)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ is_public: isPublic }),
      auth: true,
    }),

  /** Start lipsync video generation for a draft test-chat reply (in-memory status). */
  generateDraftMessageVideo: (
    draftId: string,
    tempId: string,
    text: string
  ): Promise<{ temp_id: string; status: string }> =>
    request<{ temp_id: string; status: string }>(
      `/echoes/${encodeURIComponent(draftId)}/test-chat/video/generate`,
      {
        method: 'POST',
        auth: true,
        body: JSON.stringify({ temp_id: tempId, text }),
      }
    ),

  /** Poll lipsync video generation status for a draft test-chat reply. */
  getDraftMessageVideoStatus: (
    draftId: string,
    tempId: string
  ): Promise<{
    temp_id: string;
    video_status: string;
    video_url?: string;
    video_error?: string;
  }> =>
    request<{
      temp_id: string;
      video_status: string;
      video_url?: string;
      video_error?: string;
    }>(
      `/echoes/${encodeURIComponent(draftId)}/test-chat/video/status/${encodeURIComponent(tempId)}`,
      { method: 'GET', auth: true }
    ),

  streamDraftTestChat: (
    draftId: string,
    content: string,
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const baseUrl = BASE_URL;
    const url = `${baseUrl}/echoes/${encodeURIComponent(draftId)}/test-chat-stream`;

    return fetchWithAuthRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, clear_history: false }),
      signal: signal ?? null,
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Test chat failed: ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) return;
      await readSSELines(reader, onEvent);
    });
  },

  updateVisibility: (echoId: string, data: UpdateVisibilityRequest): Promise<EchoProfileResponse> =>
    request<EchoProfileResponse>(`/echoes/${encodeURIComponent(echoId)}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  updatePricing: (echoId: string, data: UpdatePricingRequest): Promise<EchoProfileResponse> =>
    request<EchoProfileResponse>(`/echoes/${encodeURIComponent(echoId)}/pricing`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  listMyDrafts: (): Promise<EchoDraftResponse[]> =>
    request<EchoDraftResponse[]>('/echoes/my-drafts', {
      method: 'GET',
      auth: true,
    }),

  listMyEchoes: (): Promise<EchoProfileResponse[]> =>
    request<EchoProfileResponse[]>('/echoes/my-echoes', {
      method: 'GET',
      auth: true,
    }),

  deleteDraft: (draftId: string): Promise<void> =>
    request<void>(`/echoes/${encodeURIComponent(draftId)}`, {
      method: 'DELETE',
      auth: true,
    }),

  collectFromInternet: (draftId: string): Promise<CollectInternetResponse> =>
    request<CollectInternetResponse>(
      `/echoes/${encodeURIComponent(draftId)}/collect-from-internet`,
      {
        method: 'POST',
        auth: true,
      }
    ),

  // --- Video Avatar Endpoints ---

  generateVideoAvatar: (
    draftId: string,
    imageUrl?: string
  ): Promise<{ message: string; variants: number; job_ids: string[] }> =>
    request<{ message: string; variants: number; job_ids: string[] }>(
      `/echoes/${encodeURIComponent(draftId)}/video-avatar/generate`,
      {
        method: 'POST',
        auth: true,
        ...(imageUrl ? { body: JSON.stringify({ image_url: imageUrl }) } : {}),
      }
    ),

  uploadVideoAvatarImage: (
    draftId: string,
    file: File
  ): Promise<{ success: boolean; url: string; blob_name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadWithProgress<{ success: boolean; url: string; blob_name: string }>(
      `/echoes/${encodeURIComponent(draftId)}/video-avatar/upload-image`,
      formData
    );
  },

  getVideoAvatarStatus: (draftId: string): Promise<VideoAvatarStatus> =>
    request<VideoAvatarStatus>(`/echoes/${encodeURIComponent(draftId)}/video-avatar/status`, {
      auth: true,
    }),

  selectVideoAvatar: (
    draftId: string,
    variantNumber: number
  ): Promise<{ success: boolean; video_url: string }> =>
    request<{ success: boolean; video_url: string }>(
      `/echoes/${encodeURIComponent(draftId)}/video-avatar/select`,
      {
        method: 'POST',
        auth: true,
        body: JSON.stringify({ variant_number: variantNumber }),
      }
    ),

  /** Check whether training is actively running for this draft. */
  getTrainingStatus: (draftId: string): Promise<TrainingStatusResponse> =>
    request<TrainingStatusResponse>(`/echoes/${encodeURIComponent(draftId)}/training-status`, {
      method: 'GET',
      auth: true,
    }),

  /** Reconnect to an ongoing training's SSE stream (GET). Replays event history. */
  observeTrainingStream: (
    draftId: string,
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const baseUrl = BASE_URL;
    const url = `${baseUrl}/echoes/${encodeURIComponent(draftId)}/training-stream`;

    return fetchWithAuthRetry(url, {
      method: 'GET',
      signal: signal ?? null,
    }).then(async (response) => {
      if (!response.ok) {
        let errBody: Record<string, unknown> = {};
        try {
          errBody = (await response.json()) as Record<string, unknown>;
        } catch {
          // ignore
        }
        throw Object.assign(
          new Error(String(errBody['detail'] ?? errBody['message'] ?? response.statusText)),
          { ...errBody, status: response.status }
        );
      }
      const reader = response.body?.getReader();
      if (!reader) return;
      await readSSELines(reader, onEvent);
    });
  },

  /** Trigger idle video generation (silent animation for chat header / profile card). */
  generateIdleVideo: (draftId: string): Promise<{ message: string }> =>
    request<{ message: string }>(
      `/echoes/${encodeURIComponent(draftId)}/video-avatar/generate-idle`,
      { method: 'POST', auth: true }
    ),

  /** Poll idle video generation status during training. */
  getIdleVideoStatus: (draftId: string): Promise<IdleVideoStatusResponse> =>
    request<IdleVideoStatusResponse>(`/echoes/${encodeURIComponent(draftId)}/idle-video/status`, {
      auth: true,
    }),

  /** Echo IDs ranked by recent message activity (no auth required). */
  getTrendingEchoIds: (days = 7, limit = 20): Promise<TrendingEchoItem[]> =>
    request<TrendingEchoItem[]>(`/echoes/trending?days=${days}&limit=${limit}`, {
      method: 'GET',
      auth: false,
    }),

  /** Public feed of approved echoes (no auth required). */
  getPublicEchoes: (
    limit = 50,
    offset = 0,
    domain?: string,
    is_verified?: boolean
  ): Promise<EchoListResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (domain) params.append('domain', domain);
    if (is_verified !== undefined) params.append('is_verified', String(is_verified));

    return request<EchoListResponse>(`/echoes?${params.toString()}`, {
      method: 'GET',
      auth: false,
    });
  },
};
